import dayjs from 'dayjs';

import type { CashAccountBalance, CashCount, CashLedgerRow, CashPosition } from '../api/cash';
import { ledgerRowWords } from './book';
import type { CashAccountKind } from '../generated/enums';
import { formatAmount } from '../format/money';
import type { Hue } from '../vocabulary';
import { outcomeOf } from './count';
import { missesOpeningBalance } from './position';
import type { TransferDraft } from './transfer';

/**
 * The accounts board — direction A of the Cash & bank design canvas (owner, 2026-10-21), on the
 * website and the phone alike: what each account card says, the one next step it offers, and the
 * "cash in hand" figure on top.
 *
 * <p>
 * <strong>One next step per card, never a row of buttons.</strong> The till offers a count, petty
 * cash a top-up when it is below its imprest, a bank its reconciliation — the thing that account
 * needs doing, so a clerk reads the board and knows what is left of the day.
 * </p>
 */

/** The identity hue of an account's tile. Never `warning` or `danger`, which mean "look at this". */
export const accountHue: Record<CashAccountKind, Extract<Hue, 'orange' | 'purple' | 'info'>> = {
  Till: 'orange',
  PettyCash: 'purple',
  Bank: 'info',
};

/**
 * The accounts the board shows: every one in use, and a retired one only while it still holds
 * money — an empty retired account is closed, and a card for it is a card for nothing.
 */
export function boardAccounts(
  accounts: readonly CashAccountBalance[] | undefined,
): CashAccountBalance[] {
  return (accounts ?? []).filter((account) => account.isActive || account.closing !== 0);
}

/** The small line under an account's name: what lands in it, or what it is kept at. */
export function accountRole(account: CashAccountBalance, currency = ''): string {
  if (!account.isActive) {
    return 'Retired';
  }

  if (account.kind === 'PettyCash') {
    return account.floatToKeep != null
      ? `Imprest ${currency}${formatAmount(account.floatToKeep)}`
      : 'Petty cash';
  }

  if (!account.isDefault) {
    return account.kind === 'Bank' ? 'Bank account' : 'Till';
  }

  return account.kind === 'Bank' ? 'Default for UPI, card' : 'Default for cash';
}

export type BoardTone = 'muted' | 'positive' | 'warning';

export interface AccountState {
  text: string;
  tone: BoardTone;
  /** Petty cash against its imprest, 0–1; null where a bar means nothing. */
  progress: number | null;
}

/** The latest count of an account, or null. */
export function lastCount(
  counts: readonly CashCount[] | undefined,
  accountId: number,
): CashCount | null {
  let last: CashCount | null = null;

  for (const count of counts ?? []) {
    if (count.cashAccountId !== accountId) {
      continue;
    }

    if (last === null || count.countedOn.slice(0, 10) > last.countedOn.slice(0, 10)) {
      last = count;
    }
  }

  return last;
}

/** How long a bank may go unreconciled before its line turns amber — about a fortnight. */
export const reconcileDueAfterDays = 14;

/**
 * The state line on a card. Most pressing first: no float stops the figure being a balance at all;
 * then retired; then the account's own check — the count for cash, the statement for a bank.
 */
export function accountState(
  account: CashAccountBalance,
  context: {
    counts: readonly CashCount[] | undefined;
    /** The latest statement this bank is matched to, if any. */
    reconciledTo: string | null | undefined;
    today: string;
  },
  currency = '',
): AccountState {
  if (missesOpeningBalance(account)) {
    return { text: 'No opening balance yet', tone: 'warning', progress: null };
  }

  if (!account.isActive) {
    return { text: 'Takes nothing new', tone: 'muted', progress: null };
  }

  if (account.kind === 'Bank') {
    if (!context.reconciledTo) {
      return { text: 'Never reconciled', tone: 'warning', progress: null };
    }

    const to = context.reconciledTo.slice(0, 10);
    const days = dayjs(context.today).diff(dayjs(to), 'day');

    return {
      text: `Reconciled to ${dayjs(to).format('D MMM')}${days > 0 ? ` · ${days} ${days === 1 ? 'day' : 'days'}` : ''}`,
      tone: days > reconcileDueAfterDays ? 'warning' : 'muted',
      progress: null,
    };
  }

  if (account.kind === 'PettyCash' && account.floatToKeep != null && account.floatToKeep > 0) {
    const progress = Math.max(0, Math.min(1, account.closing / account.floatToKeep));

    return {
      text: `${currency}${formatAmount(account.closing)} of ${currency}${formatAmount(account.floatToKeep)}`,
      tone: progress < 1 ? 'warning' : 'muted',
      progress,
    };
  }

  return {
    ...countedLine(lastCount(context.counts, account.id), context.today, currency),
    progress: null,
  };
}

/** "Counted last night · ₹0 variance". Amber once an evening has been missed. */
export function countedLine(
  count: CashCount | null,
  today: string,
  currency = '',
): { text: string; tone: BoardTone } {
  if (count === null) {
    return { text: 'Never counted', tone: 'warning' };
  }

  const day = count.countedOn.slice(0, 10);
  const when =
    day === today
      ? 'Counted today'
      : day === dayjs(today).subtract(1, 'day').format('YYYY-MM-DD')
        ? 'Counted last night'
        : `Counted ${dayjs(day).format('D MMM')}`;

  const outcome = outcomeOf(count.variance);
  const variance =
    outcome === 'agrees'
      ? `${currency}0 variance`
      : `${outcome} ${currency}${formatAmount(Math.abs(count.variance))}`;

  const fresh = day === today || when === 'Counted last night';

  return { text: `${when} · ${variance}`, tone: fresh ? 'muted' : 'warning' };
}

export type NextStep =
  { kind: 'count' } | { kind: 'topUp'; amount: number } | { kind: 'reconcile' } | null;

/** The one thing a card offers to do next, or nothing. */
export function nextStep(
  account: CashAccountBalance,
  counts: readonly CashCount[] | undefined,
  today: string,
): NextStep {
  if (!account.isActive) {
    return null;
  }

  if (account.kind === 'Bank') {
    return { kind: 'reconcile' };
  }

  if (account.kind === 'PettyCash' && account.floatToKeep != null) {
    const short = Math.round((account.floatToKeep - account.closing) * 100) / 100;

    if (short > 0) {
      return { kind: 'topUp', amount: short };
    }
  }

  // Counted today already: the server refuses a second count, so the card offers nothing.
  return lastCount(counts, account.id)?.countedOn.slice(0, 10) === today ? null : { kind: 'count' };
}

export function nextStepLabel(step: Exclude<NextStep, null>, currency = ''): string {
  switch (step.kind) {
    case 'count':
      return 'Count now';
    case 'topUp':
      return `Top up ${currency}${formatAmount(step.amount)}`;
    case 'reconcile':
      return 'Reconcile';
  }
}

/**
 * Move money filled in for a petty-cash top-up: from the branch's default till, the shortfall
 * against the imprest. Null when there is no till to draw on.
 */
export function topUpMove(
  petty: CashAccountBalance,
  amount: number,
  accounts: readonly CashAccountBalance[] | undefined,
  today: string,
): Partial<TransferDraft> | null {
  const till = accounts?.find((a) => a.kind === 'Till' && a.isDefault && a.isActive);

  return till ? { fromId: till.id, toId: petty.id, amount: String(amount), sentOn: today } : null;
}

/**
 * "Cash in hand · till and petty cash" — notes and coin only, the canvas's first stat card; the
 * bank is on its own card. The `cash/position.ts` honesty rule holds on this narrower figure: it is
 * called cash in hand only when every till and petty cash behind it has a float.
 */
export function cashInHand(position: CashPosition | undefined): {
  amount: number;
  label: string;
  isBalance: boolean;
} {
  const drawers = (position?.accounts ?? []).filter(
    (account) => account.kind !== 'Bank' && (account.isActive || account.closing !== 0),
  );
  const isBalance = position !== undefined && !drawers.some(missesOpeningBalance);

  return {
    amount: drawers.reduce((sum, account) => sum + account.closing, 0),
    label: isBalance
      ? 'Cash in hand · till and petty cash'
      : 'Money in, less money out · till and petty cash',
    isBalance,
  };
}

/** One line of the board's "Today" list. A move is one line, not its two ledger rows. */
export interface DayLine {
  key: string;
  recordedAt: string;
  occurredOn: string;
  title: string;
  meta: string;
  /** "Counter till", or "Counter till → HDFC Bank" for a move. */
  account: string;
  moneyIn: number | null;
  moneyOut: number | null;
  /** The amount of a move, which is neither in nor out. */
  moved: number | null;
}

/**
 * The cash book's rows as the board lists them (design canvas, direction A): what, which account,
 * in or out. **A move between two of this branch's accounts reads as one line** — "Till → HDFC
 * Bank · ₹15,000 moved" — when both of its rows are on the page; listing one account, or with only
 * one half present (a move to another branch), it is that account's own in or out.
 */
export function dayLines(
  rows: readonly CashLedgerRow[],
  accountName: (id: number) => string,
): DayLine[] {
  const lines: DayLine[] = [];
  const paired = new Set<number>();

  for (const row of rows) {
    if (paired.has(row.id)) {
      continue;
    }

    const words = ledgerRowWords(row);
    const partner =
      row.sourceKind === 'Transfer' && row.sourceDocumentNumber !== ''
        ? rows.find(
            (other) =>
              other.id !== row.id &&
              !paired.has(other.id) &&
              other.sourceKind === 'Transfer' &&
              other.sourceDocumentNumber === row.sourceDocumentNumber &&
              other.direction !== row.direction &&
              other.amount === row.amount &&
              other.cashAccountId !== row.cashAccountId,
          )
        : undefined;

    if (partner) {
      paired.add(partner.id);

      const out = row.direction === 'Out' ? row : partner;
      const into = row.direction === 'Out' ? partner : row;

      lines.push({
        key: `move-${row.id}-${partner.id}`,
        recordedAt: row.recordedAt,
        occurredOn: row.occurredOn,
        title: words.title,
        meta: words.meta,
        account: `${accountName(out.cashAccountId)} → ${accountName(into.cashAccountId)}`,
        moneyIn: null,
        moneyOut: null,
        moved: row.amount,
      });
      continue;
    }

    lines.push({
      key: `row-${row.id}`,
      recordedAt: row.recordedAt,
      occurredOn: row.occurredOn,
      title: words.title,
      meta: words.meta,
      account: accountName(row.cashAccountId),
      moneyIn: row.direction === 'In' ? row.amount : null,
      moneyOut: row.direction === 'Out' ? row.amount : null,
      moved: null,
    });
  }

  return lines;
}

/** "₹2,46,900 across all four accounts" — the phone band's second line. */
export function acrossAccounts(position: CashPosition | undefined, currency = ''): string | null {
  if (!position || position.accounts.length < 2) {
    return null;
  }

  const words = ['', 'one', 'both', 'all three', 'all four', 'all five', 'all six'];
  const count = position.accounts.length;
  const said = count < words.length ? `${words[count]} accounts` : `all ${count} accounts`;

  return `${currency}${formatAmount(position.closing)} across ${said}`;
}

/**
 * The band's sentence about the day (canvas "Cash & expense flow", board 1): what is in hand, then
 * what moved. Two sentences, never a caveat — the drawers' figure is called "in hand" only when every
 * till and petty cash behind it has a float (`cashInHand`), and otherwise says what it is.
 */
export function boardSentence(
  position: CashPosition | undefined,
  moved: { moneyIn: number; moneyOut: number },
  currency = '',
): string {
  if (!position) {
    return '';
  }

  const money = (value: number) => `${currency}${formatAmount(value)}`;
  const hand = cashInHand(position);
  const drawers = position.accounts.some(
    (account) => account.kind !== 'Bank' && (account.isActive || account.closing !== 0),
  );

  const first = !drawers
    ? ''
    : hand.isBalance
      ? `${money(hand.amount)} in hand across the till and petty cash.`
      : `Money in, less money out, comes to ${money(hand.amount)} across the till and petty cash.`;

  const second =
    moved.moneyIn === 0 && moved.moneyOut === 0
      ? 'Nothing has come in or gone out today.'
      : moved.moneyOut === 0
        ? `${money(moved.moneyIn)} came in today; nothing has gone out.`
        : moved.moneyIn === 0
          ? `${money(moved.moneyOut)} went out today; nothing has come in.`
          : `${money(moved.moneyIn)} came in today and ${money(moved.moneyOut)} went out.`;

  return [first, second].filter(Boolean).join(' ');
}
